import { Box, Flex, FormatNumber, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Percentage } from 'iconoir-react';
import {
  CommissionTypes,
  hasCommissionCategoryGrid,
  hasSingleCommissionRate,
  type CommissionSettings,
} from '@fluenceur/types';
import { browseColors } from './browse-tokens';
import { formatFirstOrderCommissionSummary } from '../first-order-commission';

interface ProgramCommissionPillProps {
  commissionSettings: CommissionSettings | null;
  currency: string;
}

/**
 * Commission summary pill. Renders a percentage of revenue or a fixed amount
 * per order depending on the program's commission type. Hidden when the program
 * has no enabled commission.
 */
const ProgramCommissionPill = ({ commissionSettings, currency }: ProgramCommissionPillProps) => {
  const { t, i18n } = useTranslation();

  if (!commissionSettings?.enabled) {
    return null;
  }

  const categoryCount = hasCommissionCategoryGrid(commissionSettings)
    ? commissionSettings.categoryGrid.categories.length
    : 0;
  const singleRate = hasSingleCommissionRate(commissionSettings) ? commissionSettings : null;
  if (!categoryCount && !singleRate) return null;
  const firstOrderSummary = formatFirstOrderCommissionSummary(commissionSettings, {
    locale: i18n.language,
    currency,
    t,
  });

  const isPercentage = singleRate?.type === CommissionTypes.Percentage;
  const note = isPercentage
    ? t('programs.browse.commission.percentageNote')
    : t('programs.browse.commission.fixedNote');

  return (
    <Flex
      align="center"
      alignSelf="flex-start"
      mt="14px"
      gap="6px"
      px="12px"
      py="5px"
      borderRadius="full"
      bg={browseColors.commissionBg}
      border="1px solid"
      borderColor={browseColors.commissionBorder}
    >
      <Box display="inline-flex" color={browseColors.commissionIcon}>
        <Percentage width={13} height={13} />
      </Box>
      <Text fontSize="12.5px" fontWeight={600} color={browseColors.heading}>
        {firstOrderSummary ? (
          firstOrderSummary
        ) : categoryCount ? (
          t('programs.browse.commission.categoryCount', { count: categoryCount })
        ) : singleRate && isPercentage ? (
          `${singleRate.value}%`
        ) : singleRate ? (
          <FormatNumber value={singleRate.value} style="currency" currency={currency} />
        ) : null}{' '}
        {singleRate && !firstOrderSummary && (
          <Text as="span" fontWeight={500} color={browseColors.commissionNote}>
            {note}
          </Text>
        )}
      </Text>
    </Flex>
  );
};

export default ProgramCommissionPill;
